import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENPunchoutArrowrightlong from "@tCentro/Icons/CENPunchoutArrowrightlong";
import CENPunchoutArrowturndown from "@tCentro/Icons/CENPunchoutArrowturndown";
import LazyImage from "@tCentro/MobiusOverrides/LazyImage";
import { COLORS } from "@tCentro/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    steps = "steps",
}

interface StepInfo {
    fields: {
        image: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.steps]: StepInfo[];
    };
}

type Props = OwnProps;

export interface CENPunchoutHowWorksStyles {
    headingText?: InjectableCss;
    stepsContainer?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepCircle?: InjectableCss;
    stepImage?: InjectableCss;
    stepDescription?: InjectableCss;
    rightArrow?: IconPresentationProps;
    turnDownArrow?: IconPresentationProps;
    howWorksMainContainer?: InjectableCss;
    stepBox?: InjectableCss;
    stepNumberStyles?: InjectableCss;
    stepsInnerContainer?: InjectableCss;
    stepsInnerGridThirdIndexStyles: InjectableCss;
}

export const cenPunchoutHowWorksStyles: CENPunchoutHowWorksStyles = {
    rightArrow: {
        color: "#E0DFE0",
        css: css`
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: -30px;
            width: 42px;
            height: 26.5px;
            svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    turnDownArrow: {
        color: "#E0DFE0",
        css: css`
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: -23px;
            width: 28.87px;
            height: 42px;
            svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    stepBox: {
        css: css`
            border: 4px solid #eef3f0;
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            width: 100%;
            box-sizing: border-box;
            padding: 16px;
        `,
    },
    stepNumberStyles: {
        css: css`
            background: #eef3f0;
            max-width: 32px;
            max-height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            width: 100%;
            height: 100%;
            padding: 4px 8px;
            position: absolute;
            top: -10px;
            left: 16px;
            span {
                font-family: Poppins;
                font-weight: 600;
                font-size: 18px;
                line-height: 24px;
                text-align: center;
            }
        `,
    },
    stepsContainer: {
        css: css`
            display: grid;
            grid-template-rows: repeat(2, auto);
            grid-template-columns: repeat(4, 185px);
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-rows: repeat(3, auto);
                        grid-template-columns: repeat(2, auto);
                        place-content: center;
                        gap: 8px;
                    `,
                    css`
                        grid-template-rows: repeat(3, auto);
                        grid-template-columns: repeat(2, 185px);
                        place-content: center;
                        gap: 8px;
                    `,
                    css`
                        grid-template-columns: repeat(4, auto);
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1280px) {
                            grid-template-columns: repeat(4, auto);
                        }
                    `,
                    css``,
                ])};
        `,
    },
    stepsInnerContainer: {
        css: css`
            position: relative;
        `,
    },
    stepsInnerGridThirdIndexStyles: {
        css: css`
            position: relative;
            grid-column-start: 2;
        `,
    },

    howWorksMainContainer: {
        css: css`
            margin-bottom: 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 32px;
                    `,
                    css`
                        margin-bottom: 32px;
                    `,
                    css`
                        margin-bottom: 40px;
                    `,
                    css``,
                    css``,
                ])};
        `,
    },
    headingText: {
        css: css`
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: ${getProp("theme.typography.h1.color")};
            text-align: center;
            margin-bottom: 20px;
        `,
    },
    stepDescription: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            color: ${getColor("primary")};
            padding-top: 4px;
        `,
    },
};

const styles = cenPunchoutHowWorksStyles;

const CENPunchoutHowWorks: FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.howWorksMainContainer}>
            <StyledWrapper>
                <Typography {...styles.headingText} as="h2">
                    {translate("How it Works")}
                </Typography>
            </StyledWrapper>

            <StyledWrapper {...styles.stepsContainer}>
                {fields.steps.map((item, index) => {
                    const isMiddleStep = index === Math.floor((fields.steps.length - 1) / 2);
                    const isLastStep = index === fields.steps.length - 1;
                    const midIndex = Math.ceil(fields.steps.length / 2);
                    return (
                        <StyledWrapper
                            key={index}
                            {...(index === 3 ? styles.stepsInnerGridThirdIndexStyles : styles.stepsInnerContainer)}
                        >
                            <StyledWrapper {...styles.stepBox}>
                                <StyledWrapper {...styles.stepNumberStyles}>
                                    <Typography as="span">{index + 1}</Typography>
                                </StyledWrapper>
                                <LazyImage
                                    src={item.fields.image}
                                    {...styles.stepImage}
                                    altText={item.fields.description}
                                />
                                <Typography as="p" {...styles.stepDescription}>
                                    {item.fields.description}
                                </Typography>
                            </StyledWrapper>
                            {!isLastStep && !isMiddleStep && (
                                <Hidden below="md">
                                    <Icon src={CENPunchoutArrowrightlong} {...styles.rightArrow} />
                                </Hidden>
                            )}
                            {isMiddleStep && (
                                <Hidden below="md">
                                    <Icon src={CENPunchoutArrowturndown} {...styles.turnDownArrow} />
                                </Hidden>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENPunchoutHowWorks,
    definition: {
        group: "Common",
        displayName: "CENPunchoutHowWorks",
        fieldDefinitions: [
            {
                name: fields.steps,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => item.fields.description,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
